import { DocumentNode } from '@apollo/client';

// Types
import { SurveillanceRoute } from '../../../../types/surveillanceRoute';
import { RouteHeaderData } from '../types';

// GQL
import { client } from '../../../../gql/client';
import {
  GET_CONCESSION_BY_ID,
  GET_ROAD_BY_ID,
} from '../../../../gql/queries/geoPositions/roads';
import { GET_TEAM_BY_ID, GET_USER_BY_ID } from '../../../../gql/queries/users';
import {
  GET_VEHICLE_OCCUPANT_BY_ID,
  GET_VEHICLE_BY_ID,
} from '../../../../gql/queries/vehicles';

// ═══════════════════════════════════════════════════════════════════
// HEADER DATA — nombres resueltos para la cabecera del detalle (concesión,
// carretera, equipo, operarios, vehículos+conductor). El route solo trae
// IDs (ver SURVEILLANCE_ROUTE_FULL_FRAGMENT) — aquí se resuelven a nombre.
//
// Tolerante a fallos, no-negociable: cada query individual pasa por `q()`
// (catch → null), y la función completa se invoca envuelta en try/catch en
// el loader — un nombre que no resuelve deja ese campo en ''/[], NUNCA
// bloquea ni tira abajo la página.
//
// Extraído de `RouteDetailed/loader.ts` (Task 4, auditoría 2026-07-07,
// refactor de patrones RV) — mismo código, solo movido a su propio fichero
// para separar la resolución de datos de cabecera del loader que orquesta
// route+segments+orderRoid+headerData.
// ═══════════════════════════════════════════════════════════════════

export const EMPTY_HEADER_DATA: RouteHeaderData = {
  concessionName: '',
  roadName: '',
  roadNamesById: {},
  teamName: '',
  operators: [],
  vehicles: [],
  creatorName: '',
  ownerName: '',
};

type ResolvedUser = { id: string; name: string; surName?: string };
type ResolvedOccupant = {
  id: string;
  userDriverIDs?: string[];
  vehicleIDs?: string[];
};

interface ConcessionNameResult {
  concession: { id: string; name: string } | null;
}
interface RoadNameResult {
  road: { id: string; name: string } | null;
}
interface TeamNameResult {
  team: { id: string; name: string } | null;
}
interface UserNameResult {
  user: ResolvedUser | null;
}
interface VehicleOccupantResult {
  vehicleOccupant: ResolvedOccupant | null;
}
interface VehiclePlateResult {
  vehicle: { id: string; plate: string } | null;
}

const fullName = (u?: ResolvedUser | null): string =>
  u ? `${u.name} ${u.surName ?? ''}`.trim() : '';

/**
 * Resuelve los nombres para la cabecera del detalle de un RV a partir de sus
 * IDs (concesión, carretera, equipo, operarios, vehículos+conductor).
 *
 * Cada query pasa por `q()`, que atrapa cualquier fallo de red/GraphQL y
 * devuelve `null` — nunca deja que un `Promise.all` rechace por un campo
 * secundario.
 */
export const resolveHeaderData = async (
  route: SurveillanceRoute,
  options: { fetchPolicy?: 'cache-first' | 'cache-only' } = {}
): Promise<RouteHeaderData> => {
  // 🔴 `cache-only` es lo que usa el SNAPSHOT del detalle (caché primero, spec
  // 2026-09-11): con `cache-first`, un solo nombre sin cachear —un conductor
  // nuevo, la carretera del ramal— saldría a la red, y con cobertura degradada
  // esa petición se cuelga y vuelve a dejar la pantalla en el spinner por la
  // puerta de atrás. Lo que no esté en caché queda vacío y lo rellena la carga
  // de servidor, que va en paralelo.
  const { fetchPolicy = 'cache-first' } = options;
  const q = <T,>(
    query: DocumentNode,
    variables: Record<string, unknown>
  ): Promise<T | null> =>
    client
      .query<T>({ query, variables, fetchPolicy })
      .then((r) => r.data ?? null)
      .catch(() => null);

  const [concession, roads, team, users, occupants, creator, owner] =
    await Promise.all([
      route.concessionIDs?.[0]
        ? q<ConcessionNameResult>(GET_CONCESSION_BY_ID, {
            id: route.concessionIDs[0],
          })
        : Promise.resolve(null),
      // Multi-carretera (2026-08-15): TODAS las carreteras del recorrido. La
      // ficha del informe decía «C-25» aunque el recorrido cubriera también el
      // ramal, y ese es el único sitio donde el dato volvía a ser engañoso.
      Promise.all(
        (route.roadIDs ?? []).map((id) =>
          q<RoadNameResult>(GET_ROAD_BY_ID, { id })
        )
      ),
      route.teamIDs?.[0]
        ? q<TeamNameResult>(GET_TEAM_BY_ID, { id: route.teamIDs[0] })
        : Promise.resolve(null),
      Promise.all(
        (route.userIDs ?? []).map((id) =>
          q<UserNameResult>(GET_USER_BY_ID, { input: { id } })
        )
      ),
      Promise.all(
        (route.vehicleOccupantIDs ?? []).map((id) =>
          q<VehicleOccupantResult>(GET_VEHICLE_OCCUPANT_BY_ID, { id })
        )
      ),
      // Creador (userId) y dueño del timer (timerOwnerId) — la tablet, no la
      // cuadrilla. Mismo `q()` tolerante: si no resuelve queda ''.
      route.userId
        ? q<UserNameResult>(GET_USER_BY_ID, { input: { id: route.userId } })
        : Promise.resolve(null),
      route.timerOwnerId
        ? q<UserNameResult>(GET_USER_BY_ID, {
            input: { id: route.timerOwnerId },
          })
        : Promise.resolve(null),
    ]);

  const resolvedUsers: ResolvedUser[] = users
    .map((u) => u?.user)
    .filter((u): u is ResolvedUser => Boolean(u));

  const usersById = new Map(resolvedUsers.map((u) => [u.id, fullName(u)]));

  const resolvedOccupants: ResolvedOccupant[] = occupants
    .map((o) => o?.vehicleOccupant)
    .filter((o): o is ResolvedOccupant => Boolean(o));

  // Vehículos: occupant → plate del vehículo + nombre del conductor.
  // El conductor suele estar en userIDs (ya fetched); si no, fetch puntual.
  const vehicles = await Promise.all(
    resolvedOccupants.map(async (occupant) => {
      const vehicleId = occupant.vehicleIDs?.[0];
      const driverId = occupant.userDriverIDs?.[0];

      const vehicle = vehicleId
        ? await q<VehiclePlateResult>(GET_VEHICLE_BY_ID, { id: vehicleId })
        : null;

      let driverName = driverId ? usersById.get(driverId) ?? '' : '';
      if (driverId && !driverName) {
        const extra = await q<UserNameResult>(GET_USER_BY_ID, {
          input: { id: driverId },
        });
        driverName = fullName(extra?.user);
      }

      return { plate: vehicle?.vehicle?.plate ?? '', driverName };
    })
  );

  return {
    concessionName: concession?.concession?.name ?? '',
    // Con una sola carretera esto es exactamente el nombre de siempre.
    roadName: roads
      .map((r) => r?.road?.name)
      .filter(Boolean)
      .join(', '),
    /** Por id, para que la tabla de tramos del informe pinte la de CADA uno. */
    roadNamesById: Object.fromEntries(
      roads
        .map((r) => r?.road)
        .filter((r): r is { id: string; name: string } => Boolean(r?.id))
        .map((r) => [r.id, r.name])
    ),
    teamName: team?.team?.name ?? '',
    operators: resolvedUsers.map((u) => ({ id: u.id, fullName: fullName(u) })),
    vehicles: vehicles.filter((v) => v.plate || v.driverName),
    creatorName: fullName(creator?.user),
    ownerName: fullName(owner?.user),
  };
};

export default resolveHeaderData;
